import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { StatusPill, MonoText } from '../../ui/YoolTable';
import { Filter, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const T = {
  border: '#E0E0E0',
  head:   '#9499A0',
  hover:  '#F7F8F9',
};

const DetailedMetricsTable = ({
  data = [],
  columns = [],
  title = "Métricas Detalladas",
  description = "Tabla detallada con filtros y búsqueda",
  isLoading = false,
  className = "",
  showSearch = true,
  showFilters = true,
  showPagination = true,
  pageSize = 10,
  sortable = true
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterConfig, setFilterConfig] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const defaultColumns = [
    { key: 'name',   label: 'Nombre',    sortable: true, filterable: true },
    { key: 'value',  label: 'Valor',     sortable: true, type: 'number' },
    { key: 'trend',  label: 'Tendencia', sortable: false, type: 'trend' },
    { key: 'status', label: 'Estado',    sortable: true, filterable: true, type: 'badge' }
  ];

  const tableColumns = columns.length > 0 ? columns : defaultColumns;

  const filteredData = useMemo(() => {
    let filtered = [...data];
    if (searchTerm) {
      filtered = filtered.filter(item =>
        Object.values(item).some(v => v?.toString().toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    Object.entries(filterConfig).forEach(([key, value]) => {
      if (value && value !== 'all') filtered = filtered.filter(item => item[key] === value);
    });
    return filtered;
  }, [data, searchTerm, filterConfig]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      const av = a[sortConfig.key], bv = b[sortConfig.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number')
        return sortConfig.direction === 'asc' ? av - bv : bv - av;
      const as = av.toString().toLowerCase(), bs = bv.toString().toLowerCase();
      if (as < bs) return sortConfig.direction === 'asc' ? -1 : 1;
      if (as > bs) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    if (!showPagination) return sortedData;
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize, showPagination]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key) => {
    if (!sortable) return;
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const getFilterOptions = (key) => [...new Set(data.map(item => item[key]))].filter(v => v != null);

  const STATUS_ALIAS = {
    active: 'activo', success: 'completado',
    warning: 'pendiente', pending: 'pendiente',
    error: 'error', failed: 'fallido',
  };

  const renderCell = (item, col) => {
    const value = item[col.key];
    switch (col.type) {
      case 'number':
        return <MonoText>{typeof value === 'number' ? value.toLocaleString() : value}</MonoText>;
      case 'currency':
        return <MonoText>{typeof value === 'number' ? `${value.toLocaleString()} UF` : value}</MonoText>;
      case 'percentage':
        return <MonoText>{typeof value === 'number' ? `${value.toFixed(1)}%` : value}</MonoText>;
      case 'trend': {
        if (!value || typeof value !== 'object') return '-';
        const Icon = value.direction === 'up' ? TrendingUp : value.direction === 'down' ? TrendingDown : Minus;
        const color = value.direction === 'up' ? 'var(--yool-success)' : value.direction === 'down' ? 'var(--yool-error)' : 'var(--yool-muted)';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon style={{ width: 14, height: 14, color }} />
            <MonoText style={{ color }}>{value.percentage?.toFixed(1)}%</MonoText>
          </span>
        );
      }
      case 'badge':
        return <StatusPill estado={STATUS_ALIAS[value] || value}>{value}</StatusPill>;
      case 'date':
        return value ? new Date(value).toLocaleDateString('es-CL') : '-';
      default:
        return value || '-';
    }
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--yool-muted)', fontSize: 13 }}>
            Cargando…
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {(showSearch || showFilters) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
              {showSearch && (
                <input
                  placeholder="Buscar…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{
                    border: `1px solid ${T.border}`, borderRadius: 8,
                    padding: '7px 12px', fontSize: 13, outline: 'none',
                    minWidth: 200, color: 'var(--yool-black)', background: '#fff',
                  }}
                />
              )}
              {showFilters && (() => {
                const filterableCols = tableColumns.filter(c => c.filterable);
                if (!filterableCols.length) return null;
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {filterableCols.map(col => (
                      <Select
                        key={col.key}
                        value={filterConfig[col.key] || 'all'}
                        onValueChange={val => setFilterConfig(prev => ({ ...prev, [col.key]: val }))}
                      >
                        <SelectTrigger style={{ width: 180 }}>
                          <Filter style={{ width: 14, height: 14, marginRight: 6 }} />
                          <SelectValue placeholder={`Filtrar ${col.label}`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          {getFilterOptions(col.key).map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          <div style={{ borderRadius: 16, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {tableColumns.map(col => (
                    <th
                      key={col.key}
                      onClick={() => col.sortable && handleSort(col.key)}
                      style={{
                        padding: '10px 14px', fontSize: 11, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                        color: T.head, background: '#fff',
                        borderBottom: `1px solid ${T.border}`,
                        cursor: sortable && col.sortable ? 'pointer' : 'default',
                        userSelect: 'none', whiteSpace: 'nowrap', textAlign: 'left',
                      }}
                    >
                      {col.label}
                      {sortable && col.sortable && (
                        <span style={{ marginLeft: 4, opacity: 0.6, fontSize: 10 }}>
                          {sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={tableColumns.length} style={{ padding: '32px 0', textAlign: 'center', color: 'var(--yool-muted)', fontSize: 13 }}>
                      No se encontraron resultados
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, index) => (
                    <tr
                      key={item.id || index}
                      style={{ borderTop: `1px solid ${T.border}` }}
                      onMouseEnter={e => (e.currentTarget.style.background = T.hover)}
                      onMouseLeave={e => (e.currentTarget.style.background = '')}
                    >
                      {tableColumns.map(col => (
                        <td key={col.key} style={{ padding: '10px 14px', verticalAlign: 'middle' }}>
                          {renderCell(item, col)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {showPagination && totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--yool-muted)' }}>
                Mostrando {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, sortedData.length)} de {sortedData.length}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>Anterior</Button>
                <span style={{ color: 'var(--yool-muted)' }}>Pág. {currentPage} / {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Siguiente</Button>
              </div>
            </div>
          )}

        </div>
      </CardContent>
    </Card>
  );
};

export default DetailedMetricsTable;
